import DownloadIcon from "@mui/icons-material/Download";
import { skipToken } from "@reduxjs/toolkit/query";
import { styled } from "@mui/material";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { enumKeyOf } from "_shared/_utilities/enum-utils";
import { DocumentFileType } from "documents/document-file-type";
import { filesize } from "filesize";
import Individual from "marketplace/individuals/individual";
import { useGetMessageImagesQuery } from "messaging/_api/messaging-api";
import MessageFileInfo from "messaging/attachments/file-info/message-file-info";
import { UserAvatarMap } from "messaging/forums/_views/components/forum-chat-window";
import Message from "messaging/message";
import { enqueueSnackbar } from "notistack";
import React, { useCallback, useEffect } from "react";
import { FileIcon, defaultStyles } from "react-file-icon";
import MessageMarkdownContent from "_shared/_views/markdown/message-markdown-content";
import MessageHistoryControl from "messaging/_views/components/message-history-dialog";

const MessageContainer = styled("div")(({ theme }) => ({
  "@container (min-width: 0px)": {
    maxWidth: "90%",
  },
  "@container (min-width: 600px)": {
    maxWidth: "75%",
  },
  "@container (min-width: 800px)": {
    maxWidth: "50%",
  },
  "@container (min-width: 1000px)": {
    maxWidth: "40%",
  },
  margin: theme.spacing(2, "auto", 2, 0),
  width: "fit-content",
}));
const SystemMessageContainer = styled("div")(({ theme }) => ({
  "@container (min-width: 800px)": {
    width: "75%",
  },
  "@container (min-width: 0px)": {
    width: "100%",
  },
  margin: theme.spacing(0, "auto", 4, "auto"),
  width: "80%",
}));
const MessageInfo = styled("div")(({ theme }) => ({
  alignItems: "baseline",
  display: "flex",
  marginBottom: theme.spacing(0.5),
}));
const MessageAvatar = styled(Avatar)(({ theme }) => ({
  height: theme.spacing(4),
  marginRight: theme.spacing(2),
  width: theme.spacing(4),
}));
const SenderName = styled(Typography)(() => ({}));
const SubjectChip = styled(Chip)(({ theme }) => ({
  color: theme.palette.common.white,
  marginBottom: theme.spacing(2),
  "& .MuiChip-label": {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
}));
const MessageTime = styled(Typography)(({ theme }) => ({
  fontSize: "0.8em",
  marginLeft: theme.spacing(1.32),
}));
const MessageContent = styled("div")(({ theme }) => ({
  backgroundColor: "#d6defd",
  borderRadius: theme.spacing(2),
  borderTopLeftRadius: 0,
  display: "block",
  fontFamily: "Roboto, sans-serif",
  overflowWrap: "anywhere",
  padding: theme.spacing(3),
  whiteSpace: "normal",
  "& > p": {
    margin: 0,
  },
  "& a": {
    color: theme.palette.common.black,
  },
  "& blockquote": {
    borderLeft: "4px solid #ccc",
    color: "#ccc",
    margin: theme.spacing(2),
    paddingLeft: theme.spacing(2),
  },
  "& ul,ol": {
    listStylePosition: "inside",
    margin: 0,
    padding: 0,
  },
}));
const SystemMessageContent = styled("span")(({ theme }) => ({
  backgroundColor: "#e0e0e0",
  borderRadius: theme.spacing(0.66),
  display: "block",
  fontFamily: "Roboto, sans-serif",
  overflowWrap: "anywhere",
  textAlign: "center",
  padding: theme.spacing(2),
  whiteSpace: "normal",
  "& > p": {
    margin: 0,
  },
}));
const DeletedMessageText = styled("span")(({ theme }) => ({
  color: theme.palette.text.secondary,
  display: "block",
  fontStyle: "italic",
}));
const MessageAttachment = styled("figure")(({ theme }) => ({
  margin: 0,
  marginTop: theme.spacing(2),
  overflow: "hidden",
  width: "100%",
  "& figcaption": {
    "& .MuiListItemText-primary": {
      fontSize: "0.85em",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    "& .MuiListItemText-secondary": {
      color: "rgba(0, 0, 0, 0.65)",
      fontSize: "0.8em",
    },
  },
}));
const AttachmentPreview = styled(Link)(() => ({
  cursor: "pointer",
}));
const FileTypeIconContainer = styled("div")(({ theme }) => ({
  width: theme.spacing(8),
}));
const LoadingIndicator = styled(CircularProgress)(({ theme }) => ({
  color: "black",
  display: "flex",
  height: theme.spacing(5),
  width: theme.spacing(5),
}));
const ImageAttachment = styled("img")(() => ({
  width: "100%",
}));
const DownloadButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: "rgba(0, 0, 0, 0.2)",
  color: theme.palette.common.white,
}));

type ReceivedMessageProps = {
  message: Message;
  sender?: Individual;
  avatars?: UserAvatarMap;
  onImageLoad?: (event: React.SyntheticEvent<HTMLImageElement>) => void;
  onImageClick: (message: Message, image: MessageFileInfo) => void;
  onDownload: (message: Message, attachment: MessageFileInfo) => void;
};

export const ReceivedMessage = React.forwardRef<
  HTMLDivElement,
  ReceivedMessageProps & React.HTMLAttributes<HTMLDivElement>
>(function ReceivedMessage(props, ref) {
  const { message, sender, avatars, onImageLoad, onImageClick, onDownload, ...otherProps } = props;

  const hasImagesAttached = message.attachments?.some((attachment: MessageFileInfo) => attachment.isImage) ?? false;
  const forumId = message.forum.id?.value;
  const messageId = message.id?.value;

  const imageQueryArgs = hasImagesAttached && forumId !== undefined && messageId !== undefined
    ? { forumId, messageId }
    : skipToken;
  const {
    data: imageDataResponse,
    isFetching: isLoadingAttachment,
    isError: isImageLoadError,
  } = useGetMessageImagesQuery(imageQueryArgs);
  const imageData = React.useMemo(() => imageDataResponse ?? [], [imageDataResponse]);
  const isRemoved = message.isDeleted || Boolean(message.retracted);
  const historyLabel = message.isDeleted ? "Deleted" : message.retracted ? "Removed" : "Edited";

  useEffect(() => {
    if (!isImageLoadError) return;
    try {
      throw new Error("Failed to get message images");
    } catch {
      enqueueSnackbar("Failed to get message attachment. Please try again.", { variant: "error" });
    }
  }, [isImageLoadError]);

  const handleAttachmentClick = useCallback((attachment: MessageFileInfo) => {
    if (isLoadingAttachment) return;

    const imageAttachment = imageData.find((image: MessageFileInfo) => image.id.isEqualTo(attachment.id));
    if (attachment.isImage && imageAttachment) {
      onImageClick(message, imageAttachment);
    } else {
      onDownload(message, attachment);
    }
  }, [imageData, isLoadingAttachment, message, onDownload, onImageClick]);

  return (
    <>
      {message?.senderId == null ? (
        <SystemMessageContainer ref={ref} {...otherProps}>
          <MessageInfo style={{ justifyContent: "center" }}>
            {!message.isDeleted && (message.retracted || message.updated) && (
              <MessageHistoryControl message={message} label={historyLabel} canViewHistory={!isRemoved} />
            )}
            <MessageTime>
              <time>{message.published?.format("MM/DD/YY hh:mm A")}</time>
            </MessageTime>
          </MessageInfo>
          <div>
            {message?.senderId && message.subject && message.subject.length > 0 && (
              <SubjectChip label={`RE: ${message.subject}`} size="small" />
            )}
          </div>
          <SystemMessageContent>
            {message.isDeleted ? (
              <DeletedMessageText>This message was deleted</DeletedMessageText>
            ) : (
              <MessageMarkdownContent content={message.content ?? ""} />
            )}
            {message.attachments?.map((attachment: MessageFileInfo) => (
              <MessageAttachment key={attachment.id.value}>
                {isLoadingAttachment && <LoadingIndicator disableShrink thickness={2} />}
                {!isLoadingAttachment && (
                  <AttachmentPreview onClick={() => handleAttachmentClick(attachment)}>
                    {attachment?.isImage ? (
                      <ImageAttachment alt={attachment.name} src={attachment.dataUri} />
                    ) : (
                      <FileTypeIconContainer>
                        <FileIcon
                          extension={enumKeyOf(DocumentFileType, attachment.name.split(".")[1]).toLocaleLowerCase()}
                          {...defaultStyles[attachment.name.split(".")[1].toLocaleLowerCase() as keyof typeof defaultStyles]}
                        />
                      </FileTypeIconContainer>
                    )}
                  </AttachmentPreview>
                )}
                <figcaption>
                  <ListItemText primary={attachment.name} secondary={filesize(attachment.fileSize)} />
                </figcaption>
              </MessageAttachment>
            ))}
          </SystemMessageContent>
        </SystemMessageContainer>
      ) : (
        <MessageContainer ref={ref} {...otherProps}>
          <MessageInfo>
            {
              <>
                <MessageAvatar src={avatars?.[message?.senderId.value]} />
                <SenderName>
                  {sender?.profile?.firstName ?? "Unknown"} {sender?.profile?.lastName ?? "Sender"}
                </SenderName>
              </>
            }
            {message.isDeleted ? null : message.retracted ? (
              <MessageHistoryControl message={message} label={historyLabel} canViewHistory={false} />
            ) : message.updated ? (
              <MessageHistoryControl message={message} label="Edited" />
            ) : null}
            <MessageTime>
              <time>{message.published?.format("MM/DD/YY hh:mm A")}</time>
            </MessageTime>
          </MessageInfo>
          <MessageContent>
            <div>
              {message?.senderId && message.subject && message.subject.length > 0 && (
                <SubjectChip label={`RE: ${message.subject}`} size="small" />
              )}
            </div>
            {message.isDeleted ? (
              <DeletedMessageText>This message was deleted</DeletedMessageText>
            ) : (
              <MessageMarkdownContent content={message.content ?? ""} />
            )}
            {message.attachments?.map((attachment: MessageFileInfo) => (
              <MessageAttachment key={attachment.id.value}>
                {isLoadingAttachment && <LoadingIndicator disableShrink thickness={2} />}
                {!isLoadingAttachment && (
                  <AttachmentPreview onClick={() => handleAttachmentClick(attachment)}>
                    {attachment?.isImage ? (
                      <ImageAttachment
                        alt={attachment.name}
                        src={imageData.find((image: MessageFileInfo) => image.id.isEqualTo(attachment.id))?.dataUri}
                        onLoad={onImageLoad}
                      />
                    ) : (
                      <FileTypeIconContainer>
                        <FileIcon
                          extension={enumKeyOf(DocumentFileType, attachment.name.split(".")[1]).toLocaleLowerCase()}
                          {...defaultStyles[attachment.name.split(".")[1].toLocaleLowerCase() as keyof typeof defaultStyles]}
                        />
                      </FileTypeIconContainer>
                    )}
                  </AttachmentPreview>
                )}
                <figcaption>
                  <ListItem
                    disableGutters
                    secondaryAction={
                      <Tooltip title="Download">
                        <span>
                          <DownloadButton
                            aria-label="download"
                            size="medium"
                            onClick={() => onDownload(message, attachment)}
                          >
                            <DownloadIcon fontSize="medium" />
                          </DownloadButton>
                        </span>
                      </Tooltip>
                    }
                  >
                    <ListItemText primary={attachment.name} secondary={filesize(attachment.fileSize)} />
                  </ListItem>
                </figcaption>
              </MessageAttachment>
            ))}
          </MessageContent>
        </MessageContainer>
      )}
    </>
  );
});
